import { useCallback, useEffect, useMemo, useState } from "react";
import ChatbotMainPanel from "./components/ChatbotMainPanel";
import ChatbotSidePanel from "./components/ChatbotSidePanel";
import { ChatDataType } from "./constant";
import { formatChatHistory, formatBotResponse } from "./utils";
import "./chatbot.scss";
import useGetChatDetail from "../../../../Hooks/useGetChatDetail";
import usePostChat from "../../../../Hooks/usePostChat";
import useSSEStreamChat from "../../../../Hooks/useSSEStreamChat";
import useGetChatList from "../../../../Hooks/useGetChatList";
import useResetMemory from "../../../../Hooks/useResetMemory";
import FailModal from "../../../../Components/FailModal";
import useStreamContent from "../../../../Hooks/useStreamContent";
import useGetUserChatToken from "../../../../Hooks/useGetUserChatToken";
import { useNavigate } from "react-router-dom";
import useGetAccountInfo from "../../../../Hooks/useGetAccountInfo";

const Chatbot = () => {
  const navigate = useNavigate();
  const resetMemory = useResetMemory();
  const {
    data: userChatToken,
    loading: loadingUserChatToken,
    getData: getUserChatToken,
  } = useGetUserChatToken();
  const { data: chatList, isFetched: isFetchedChatList } = useGetChatList();
  const { userInfo } = useGetAccountInfo();

  const [lastId, setLastId] = useState();

  const {
    data: initialChatData,
    loading: isLoadingInitialChatData,
    isFetched: isFetchedInitialChatData,
  } = useGetChatDetail({ id: lastId });
  const postChat = usePostChat();
  const sseStreamChat = useSSEStreamChat();

  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState("");
  const [chatData, setChatData] = useState([]);

  const [sidePanel, setSidePanel] = useState({
    show: false,
    data: null,
  });

  const [isWaitingResponse, setIsWaitingResponse] = useState(false);

  const [isResettingChat, setIsResettingChat] = useState(false);

  const [errorInfo, setErrorInfo] = useState({
    show: false,
    title: "Reset Agent",
    description:
      "This will permanently delete your saved wedding date, budget, location, and all other planning details. Are you sure you want to start from scratch?",
    cancelText: "Cancel",
    retryText: "Reset",
    hideIcon: true,
  });

  const [insufficientTokenModal, setInsufficientTokenModal] = useState({
    show: false,
    title: "Insufficient Token",
    description:
      "You’ve used all your available tokens. To continue using WeddinglyAi Agent, please subscribe to one of our plans.",
    cancelText: "Close",
    retryText: "View Plans",
  });

  const [disableSubmit, setDisableSubmit] = useState(false);

  const { streamingContent: cleanedStreamingContent, isParsing } =
    useStreamContent(streamingContent);

  const onMutateSidePanel = (mutatedData) => {
    setSidePanel(mutatedData);
    window.dispatchEvent(new CustomEvent("mutateSidePanel"));
  };

  const handleResetChat = useCallback(() => {
    setIsResettingChat(true);
    setErrorInfo((prev) => ({ ...prev, show: false }));

    resetMemory(lastId)
      .then(() => {
        setChatData([]);
        onMutateSidePanel({
          show: false,
          data: null,
        });
        setShowSidePanel(false);
        sessionStorage.removeItem("chatId");
        setIsWaitingResponse(false);
      })
      .catch(console.error)
      .finally(() => {
        setIsResettingChat(false);
      });
  }, [lastId, resetMemory]);

  const isLoading =
    !isFetchedChatList || isLoadingInitialChatData || isResettingChat;

  useEffect(() => {
    if (chatList.length > 0) {
      setLastId(chatList[chatList.length - 1]?.id);
    }
  }, [chatList]);

  useEffect(() => {
    if (isFetchedInitialChatData) {
      if (initialChatData.length > 0) {
        const { chatHistory, weddingPlan } = formatChatHistory(initialChatData);
        setChatData(chatHistory);

        if (weddingPlan) {
          onMutateSidePanel({
            show: true,
            data: weddingPlan,
          });
        }
      }

      const promptText = sessionStorage.getItem("promptText");
      if (promptText) {
        handleSubmit(promptText);
        sessionStorage.removeItem("promptText");
      }
    } else if (isFetchedChatList && chatList.length === 0) {
      const promptText = sessionStorage.getItem("promptText");
      if (promptText) {
        handleSubmit(promptText);
        sessionStorage.removeItem("promptText");
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialChatData, isFetchedInitialChatData, isFetchedChatList]); // will not include handleSubmit since we will wrap many function in useCallback

  const handleSubmit = async (value) => {
    if (!sessionStorage.getItem("chatId")) {
      sessionStorage.setItem("chatId", lastId);
    }

    setChatData((prev) => {
      const cleanPrev = prev.filter(
        (item) =>
          item.type !== ChatDataType.DateInput &&
          item.type !== ChatDataType.Chips
      );

      return [
        ...cleanPrev,
        {
          type: ChatDataType.Text,
          bot: false,
          content: value,
          timestamp: new Date().getTime(),
        },
      ];
    });

    try {
      setIsWaitingResponse(true);
      const res = await postChat({
        prompt: value,
        chatId: lastId,
      });

      setLastId(res.id);

      const { onMessage, onError, close } = sseStreamChat({ chatId: res.id });

      let content = "";

      onMessage((message) => {
        setIsStreaming(true);
        setIsWaitingResponse(false);
        try {
          const currentContent =
            typeof message === "string"
              ? message
              : JSON.parse(message.data)?.answer || "";
          content += currentContent;
          setStreamingContent((prev) => prev + currentContent);
        } catch (err) {
          console.error("cannot parse message", { message, err });
        }
      });

      onError((err) => {
        setIsWaitingResponse(false);
        setIsStreaming(false);
        setStreamingContent("");

        const { formattedResponse, weddingPlan } = formatBotResponse(
          content,
          new Date().getTime()
        );
        setChatData((prev) => {
          return [...prev, ...formattedResponse];
        });

        if (weddingPlan) {
          onMutateSidePanel({
            show: true,
            data: weddingPlan,
          });
        }
        close();
        getUserChatToken()
          .then((res) => {
            const isLimitReached = res?.usage >= res?.maxAmount;
            if (isLimitReached) {
              setInsufficientTokenModal((prev) => ({ ...prev, show: true }));
              setDisableSubmit(true);
            }
          })
          .catch((err) => console.error(err));
      });
    } catch (err) {
      console.error(err);
      if (
        err?.response?.status === 500 &&
        err?.response?.data?.detail === "Insufficient token"
      ) {
        setInsufficientTokenModal((prev) => ({ ...prev, show: true }));
        setDisableSubmit(true);
      }
      setIsWaitingResponse(false);
    }
  };

  const handleCancelFailModal = () => {
    setErrorInfo({ ...errorInfo, show: false });
  };

  const renderFooter = useMemo(() => {
    return (
      <>
        <span onClick={handleCancelFailModal}>{errorInfo.cancelText}</span>
        <span onClick={handleResetChat}>{errorInfo.retryText}</span>
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorInfo, handleResetChat]); // will not include handleCancelFailModal since we will wrap many function in useCallback

  const initialParsing = isParsing && !sidePanel.show && !isWaitingResponse;

  const [showSidePanel, setShowSidePanel] = useState(false);

  useEffect(() => {
    if (sidePanel.show || isParsing) {
      setShowSidePanel(true);
    }
  }, [sidePanel.show, isParsing]);

  const userIsGreeted = useMemo(() => userInfo?.userIsGreeted, [userInfo]);

  return (
    <div className="chatbot-container">
      <div className="chatbot">
        <ChatbotMainPanel
          userIsGreeted={userIsGreeted}
          chatData={chatData}
          onSubmit={handleSubmit}
          isWaitingResponse={isWaitingResponse}
          loading={isLoading}
          parsing={isParsing && !initialParsing}
          showSidePanel={showSidePanel}
          isStreaming={isStreaming}
          streamingContent={cleanedStreamingContent}
          onResetChat={() => setErrorInfo({ ...errorInfo, show: true })}
          userChatToken={userChatToken}
          isLoadingUserChatToken={loadingUserChatToken}
          getUserChatToken={getUserChatToken}
          disableSubmit={disableSubmit}
        />

        {showSidePanel && (
          <ChatbotSidePanel
            chatId={lastId}
            panelData={sidePanel.data}
            initialParsing={initialParsing}
            isParsing={isParsing}
            disableAction={
              disableSubmit ||
              isWaitingResponse ||
              isStreaming ||
              loadingUserChatToken
            }
            onErroDataParsing={() => setShowSidePanel(false)}
          />
        )}
      </div>

      {/* intented to reverse the retry and cancel */}
      <FailModal
        {...errorInfo}
        onCancel={handleCancelFailModal}
        customFooter={renderFooter}
        key="error-modal"
      />

      <FailModal
        {...insufficientTokenModal}
        onCancel={() =>
          setInsufficientTokenModal({ ...insufficientTokenModal, show: false })
        }
        onRetry={() => navigate("/Home/Profile?selectedTab=credit")}
        key="insufficient-token-modal"
      />
    </div>
  );
};
export default Chatbot;
